import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { ShieldAlert, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { SupervisorQueue } from "@/components/supervisor/supervisor-queue";
import { useSupervisorWebSocket } from "@/hooks/use-supervisor-ws";

const reviewSearchSchema = z.object({
  highRisk: z.boolean().optional(),
  projectId: z.string().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
});

export type ReviewSearch = z.infer<typeof reviewSearchSchema>;

export const Route = createFileRoute("/_authenticated/review")({
  validateSearch: reviewSearchSchema,
  component: ReviewPage,
});

function ReviewPage() {
  const { user } = Route.useRouteContext();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const role = user.role || "field_personnel";
  const canReview = ["admin", "div_admin", "office_personnel"].includes(role);

  const filters = {
    highRiskOnly: search.highRisk ?? false,
    projectId: search.projectId ?? null,
    dateFrom: search.dateFrom ?? null,
    dateTo: search.dateTo ?? null,
  };

  const setFilters = (next: typeof filters) => {
    navigate({
      search: {
        highRisk: next.highRiskOnly || undefined,
        projectId: next.projectId || undefined,
        dateFrom: next.dateFrom || undefined,
        dateTo: next.dateTo || undefined,
      },
      replace: true,
    });
  };

  // WebSocket for realtime supervisor queue notifications
  const { status: wsStatus } = useSupervisorWebSocket(user.id, {
    enabled: canReview,
    showToasts: true,
  });

  if (!canReview) {
    return (
      <div className="space-y-6 max-w-lg mx-auto">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <ShieldAlert className="h-12 w-12 text-muted-foreground/40 mb-3" />
            <p className="text-lg font-medium text-muted-foreground">
              Access Denied
            </p>
            <p className="text-sm text-muted-foreground/70 mt-1">
              Only foremen, managers, and admins can access the review queue.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-lg mx-auto">
      <div className="flex items-center justify-between pt-2">
        <h1 className="text-2xl font-bold text-text-primary">
          Review Queue
        </h1>
        {wsStatus === "reconnecting" && (
          <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
            <Loader2 className="h-3 w-3 animate-spin" />
            Reconnecting...
          </div>
        )}
      </div>
      <SupervisorQueue filters={filters} onFiltersChange={setFilters} />
    </div>
  );
}
